// vg-binding: P7.D-132
//
// apps/vendor/src/features/cmdk/CommandPalette.tsx
//
// Phase 7 Wave 6 Task 22 — command palette dialog.
//
// Mounts globally; listens for the `vendor:cmdk:open` CustomEvent (fired
// by VendorTopBar Cmd+K trigger in Task 06). Provides quick navigation
// to common routes — Tasks/Pages/Settings — with substring filtering.
// Recent-actions persistence is deferred to a follow-up.

import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Input } from '@printway/ui';

import { useCmdkStore } from './cmdkStore.js';

interface CommandItem {
  id: string;
  labelKey: string;
  shortcut?: string;
  to: string;
  testId: string;
}

const COMMANDS: readonly CommandItem[] = [
  { id: 'nav-dashboard', labelKey: 'cmdk.items.dashboard', to: '/', testId: 'cmdk-item-dashboard' },
  { id: 'nav-orders', labelKey: 'cmdk.items.orders', to: '/orders', testId: 'cmdk-item-orders' },
  { id: 'nav-orders-urgent', labelKey: 'cmdk.items.ordersUrgent', to: '/orders?filter=urgent', testId: 'cmdk-item-orders-urgent' },
  { id: 'nav-products', labelKey: 'cmdk.items.products', to: '/products', testId: 'cmdk-item-products' },
  { id: 'nav-inventory', labelKey: 'cmdk.items.inventory', to: '/inventory', testId: 'cmdk-item-inventory' },
  { id: 'nav-notifications', labelKey: 'cmdk.items.notifications', to: '/notifications', testId: 'cmdk-item-notifications' },
  { id: 'nav-settings', labelKey: 'cmdk.items.settings', to: '/settings/profile', testId: 'cmdk-item-settings' },
];

export function CommandPalette(): JSX.Element | null {
  const { t } = useTranslation('common');
  const open = useCmdkStore((s) => s.open);
  const query = useCmdkStore((s) => s.query);
  const closePalette = useCmdkStore((s) => s.closePalette);
  const openPalette = useCmdkStore((s) => s.openPalette);
  const setQuery = useCmdkStore((s) => s.setQuery);
  const recordRecent = useCmdkStore((s) => s.recordRecent);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);

  // External trigger — TopBar dispatches `vendor:cmdk:open` (Task 06).
  useEffect(() => {
    function onTrigger(): void {
      openPalette();
    }
    window.addEventListener('vendor:cmdk:open', onTrigger);
    return () => window.removeEventListener('vendor:cmdk:open', onTrigger);
  }, [openPalette]);

  // Focus the input when opened.
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  // ESC closes.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape' && open) closePalette();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, closePalette]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length === 0) return COMMANDS;
    return COMMANDS.filter((c) => t(c.labelKey).toLowerCase().includes(q));
  }, [query, t]);

  if (!open) return null;

  function runCommand(cmd: CommandItem): void {
    recordRecent(cmd.id);
    closePalette();
    navigate(cmd.to);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="cmdk-palette"
      /* dark-ok: modal scrim, translucent black reads correctly in both modes */
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-[10vh]"
      onClick={closePalette}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl overflow-hidden rounded-xl border border-pw-border bg-pw-bg shadow-2xl"
      >
        <div className="border-b border-pw-border p-3">
          <Input
            ref={inputRef}
            type="text"
            data-testid="cmdk-input"
            placeholder={t('cmdk.inputPlaceholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="border-pw-border bg-pw-bg text-pw-text focus:border-pw-primary focus:outline-none focus:ring-2 focus:ring-pw-primary"
          />
        </div>
        <ul className="max-h-80 overflow-auto py-1">
          {filtered.length === 0 ? (
            <li className="px-3 py-4 text-center text-sm text-pw-text-muted">
              {t('cmdk.noResults')}
            </li>
          ) : (
            filtered.map((c) => (
              <li key={c.id}>
                <Button
                  type="button"
                  variant="ghost"
                  data-testid={c.testId}
                  onClick={() => runCommand(c)}
                  className="flex h-auto w-full items-center justify-between gap-3 rounded-none px-3 py-2 text-left text-sm font-normal text-pw-text transition-colors hover:bg-pw-surface"
                >
                  <span>{t(c.labelKey)}</span>
                  <span className="font-mono text-xs text-pw-text-muted">{c.to}</span>
                </Button>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
}

export default CommandPalette;
